import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";

import { useStyles } from "./styles";

/**
 * Camera capture flow (Requirement 2).
 *
 * - Requests the camera via navigator.mediaDevices.getUserMedia({ video: true }).
 * - On denied / no camera, silently returns to the management view (no error
 *   message) per Req 2.2 / 2.3.
 * - After capturing a frame, shows a preview with Upload / Cancel.
 *   Upload -> onUpload(base64DataUrl); Cancel -> discard and resume the camera.
 */
const CameraCapture = ({ onUpload, onClose, uploading, uploadError }) => {
  const classes = useStyles();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [preview, setPreview] = useState(null); // base64 data URL
  const [starting, setStarting] = useState(true);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setStarting(true);
    // No camera API available -> silently close (Req 2.3).
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      onClose();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setStarting(false);
    } catch (err) {
      // Permission denied (NotAllowedError/PermissionDeniedError) or no camera
      // (NotFoundError/DevicesNotFoundError): silently return, no error (Req 2.2/2.3).
      stopStream();
      onClose();
    }
  }, [onClose, stopStream]);

  useEffect(() => {
    startCamera();
    return () => stopStream();
  }, [startCamera, stopStream]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg");
    setPreview(dataUrl);
    stopStream(); // pause the live feed while previewing
  };

  const handleUpload = () => {
    if (preview) {
      onUpload(preview);
    }
  };

  // Cancel from the preview: discard and resume the camera (Req 2.7).
  const handleCancelPreview = () => {
    setPreview(null);
    startCamera();
  };

  return (
    <Box className={classes.cameraContainer} data-testid="camera-capture">
      {preview ? (
        <>
          <img
            className={classes.cameraPreview}
            src={preview}
            alt="Captured preview"
            data-testid="camera-preview"
          />
          {uploadError && (
            <Typography
              color="error"
              variant="body2"
              className={classes.inlineError}
              data-testid="camera-upload-error"
            >
              {uploadError}
            </Typography>
          )}
          <Box className={classes.cameraActions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={uploading}
              data-testid="camera-upload-button"
            >
              {uploading ? <CircularProgress size={20} /> : "Upload"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancelPreview}
              disabled={uploading}
              data-testid="camera-cancel-button"
            >
              Cancel
            </Button>
          </Box>
        </>
      ) : (
        <>
          {starting && (
            <Box className={classes.cameraLoading} data-testid="camera-loading">
              <CircularProgress size={24} />
            </Box>
          )}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            className={classes.cameraVideo}
            autoPlay
            playsInline
            data-testid="camera-video"
          />
          <Box className={classes.cameraActions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCapture}
              disabled={starting}
              data-testid="camera-capture-button"
            >
              Capture
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              data-testid="camera-close-button"
            >
              Close
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

CameraCapture.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  uploadError: PropTypes.string,
};

CameraCapture.defaultProps = {
  uploading: false,
  uploadError: null,
};

export default CameraCapture;
